import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GitBranch, Plus, CalendarDays, MapPin } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { inp, lbl } from "../gear/GearBits";

/* Side trips: stand-alone trips (own timeline, packing, expenses) linked to a parent trip —
   e.g. domestic & international hops inside a round-India journey. */

export default function SideTrips({ trip, readOnly }) {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [f, setF] = useState({ title: "", start: "", end: "" });
  const side = trip.side_trips || [];
  if (trip.parent) return null; // one level of nesting keeps things simple
  if (readOnly && !side.length) return null;

  const create = async () => {
    if (!f.title.trim()) { toast.error("Give the side trip a title"); return; }
    try {
      const { data } = await api.post("/trips", {
        title: f.title.trim(), start_date: f.start || null, end_date: f.end || null,
        parent_trip_id: trip.id, description: `Side trip of "${trip.title}".`,
      });
      toast.success("Side trip created — add its flights, hotels and details");
      navigate(`/trips/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create side trip"); }
  };

  return (
    <section className="mb-10" data-testid="side-trips">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <h2 className="font-heading font-900 text-violet-700 text-2xl flex items-center gap-2">
          <GitBranch className="w-6 h-6" /> Side trips
        </h2>
        {!readOnly && (
          <button onClick={() => setCreating((v) => !v)} data-testid="side-trip-toggle"
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border border-violet-500 text-violet-700 hover:bg-violet-50">
            <Plus className="w-4 h-4" /> New side trip
          </button>
        )}
      </div>
      <p className="text-sm text-muted-foreground -mt-2 mb-4">
        Hops inside this journey — each side trip is a full stand-alone trip (own timeline, packing
        &amp; expenses) that stays connected to <b>{trip.title}</b>.
      </p>

      {creating && (
        <div className="border border-violet-300 bg-violet-50/40 p-3 mb-4 flex flex-wrap gap-2 items-end" data-testid="side-trip-form">
          <div className="flex-1 min-w-[180px]">
            <label className={lbl}>Title</label>
            <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} maxLength={80}
              className={inp} placeholder="e.g. Goa beach hop" data-testid="side-trip-title" />
          </div>
          <div><label className={lbl}>Start</label>
            <input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className={inp} data-testid="side-trip-start" /></div>
          <div><label className={lbl}>End</label>
            <input type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className={inp} data-testid="side-trip-end" /></div>
          <button onClick={create} data-testid="side-trip-create"
            className="px-4 py-2 bg-violet-700 text-white text-sm font-700 hover:bg-violet-600">Create</button>
        </div>
      )}

      {side.length === 0 ? (
        !creating && (
          <div className="border border-dashed border-zinc-300 p-6 text-center text-sm text-muted-foreground">
            No side trips yet — use <b>New side trip</b>, or <b>Import legs (AI)</b> on the Trip Timeline
            and choose “Create linked side trip”.
          </div>
        )
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {side.map((s) => (
            <Link key={s.id} to={`/trips/${s.id}`} data-testid={`side-trip-card-${s.id}`}
              className="border border-violet-200 bg-violet-50/30 p-4 hover:border-violet-500 transition-colors">
              <div className="font-heading font-700 leading-tight">{s.title}</div>
              {(s.start_date || s.end_date) && (
                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  <CalendarDays className="w-3.5 h-3.5" /> {s.start_date}{s.end_date && s.end_date !== s.start_date ? ` → ${s.end_date}` : ""}
                </div>
              )}
              {s.stops.length > 0 && (
                <div className="text-xs text-zinc-600 mt-0.5 flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" /> {s.stops.slice(0, 4).join(" → ")}
                </div>
              )}
              <div className="text-[10px] font-mono uppercase tracking-widest text-violet-600 mt-2">
                {s.segments} leg{s.segments !== 1 ? "s" : ""} · open trip →
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
